import { DNDFieldsEnum } from "../../constants/Types";

/** Normalize dashboard list sections (array, single row object, or empty). */
export const normalizePayloadList = (value: unknown): any[] | null => {
  if (Array.isArray(value)) return value;
  if (value != null && typeof value === "object") return [value];
  return null;
};

export const deleteItem = (list: any, id: number) => {
  const items = normalizePayloadList(list) ?? [];
  return items.filter((item: any) => item.id !== id);
};

export const checkDuplicate = (list: any, code: string, value: any) => {
  const items = normalizePayloadList(list) ?? [];
  return items.some((ll: any) => ll[code] === value);
};

const normalizePostTypeNumber = (item: any): number => {
  const raw = item?.[DNDFieldsEnum.POSTTYPECODE] ?? item?.postTypeNumber;
  const num = Number(raw);
  return Number.isNaN(num) ? 0 : num;
};

/** Next suggested post type number across one or more lists. */
export const getNextPostTypeNumber = (
  ...lists: (unknown[] | null | undefined)[]
): number => {
  let max = 0;
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const num = normalizePostTypeNumber(item);
      if (num > max) max = num;
    }
  }
  return max + 1;
};

/** True if post type number exists in any list (optional per-list id to exclude). */
export const isPostTypeNumberUsed = (
  postTypeNumber: number | string | null | undefined,
  lists: {
    items?: unknown[] | null;
    excludeId?: string | number | null;
  }[],
): boolean => {
  if (
    postTypeNumber === null ||
    postTypeNumber === undefined ||
    postTypeNumber === ""
  ) {
    return false;
  }
  const target = Number(postTypeNumber);
  if (Number.isNaN(target)) return false;

  return lists.some(({ items, excludeId }) => {
    if (!Array.isArray(items)) return false;
    return items.some((item: any) => {
      if (
        excludeId != null &&
        item?.id != null &&
        String(item.id) === String(excludeId)
      ) {
        return false;
      }
      return normalizePostTypeNumber(item) === target;
    });
  });
};

const normalizeItemNumber = (item: any): number => {
  const raw = item?.[DNDFieldsEnum.ITEMNUMBER];
  const num = Number(raw);
  return Number.isNaN(num) ? 0 : num;
};

/** Next suggested item number across one or more lists. */
export const getNextItemNumber = (
  ...lists: (unknown[] | null | undefined)[]
): number => {
  let max = 0;
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const num = normalizeItemNumber(item);
      if (num > max) max = num;
    }
  }
  return max + 1;
};

/** True if item number exists in any list (optional per-list id to exclude). */
export const isItemNumberUsed = (
  itemNumber: number | string | null | undefined,
  lists: {
    items?: unknown[] | null;
    excludeId?: string | number | null;
  }[],
): boolean => {
  if (itemNumber === null || itemNumber === undefined || itemNumber === "") {
    return false;
  }
  const target = Number(itemNumber);
  if (Number.isNaN(target)) return false;

  return lists.some(({ items, excludeId }) => {
    if (!Array.isArray(items)) return false;
    return items.some((item: any) => {
      if (
        excludeId != null &&
        item?.id != null &&
        String(item.id) === String(excludeId)
      ) {
        return false;
      }
      return normalizeItemNumber(item) === target;
    });
  });
};

export const fillAutocompleteValues = (
  data: unknown,
  codeField: DNDFieldsEnum,
  nameField: DNDFieldsEnum | "",
): string[] => {
  if (data && Array.isArray(data)) {
    return data.flatMap((item: any) => {
      const code = item[codeField] || "";
      const name = nameField ? item[nameField] || "" : "";

      if (code && name) return [`${code} - ${name}`];
      const value = code || name;
      if (value === "" || value == null) return [];
      return [String(value)];
    });
  }
  return [];
};

export const checkData = (data: any, selectedId: string | number | null) => {
  return Array.isArray(data)
    ? data.filter((item: any) => String(item.id) === String(selectedId))
        .length > 0
    : false;
};

export const updateData = (data: any) => {
  return data?.map((item: any, index: number) => {
    return {
      ...item,
      id: index + 1,
    };
  });
};

/** Append one row to a normalized list section (safe when prev is object or array). */
export const appendPayloadListItem = (
  prev: unknown,
  item: Record<string, unknown>,
): any[] => {
  const existing = normalizePayloadList(prev) ?? [];
  return updateData([
    ...existing,
    {
      id: existing.length + 1,
      ...item,
    },
  ]);
};

/** Update one row by id in a normalized list section. */
export const updatePayloadListItem = (
  prev: unknown,
  selectedId: string | number | null,
  item: Record<string, unknown>,
): any[] => {
  const existing = normalizePayloadList(prev) ?? [];
  return updateData(
    existing.map((entry: any) =>
      String(entry.id) === String(selectedId) ? { ...entry, ...item } : entry,
    ),
  );
};
